import { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const LABEL = "mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75";

// One-of-two tick boxes; ticking the selected box again clears it.
const Ticks = ({ name, value, options, onChange }) => (
  <div className="flex flex-wrap gap-5">
    {options.map(([v, l]) => (
      <label key={v} className="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" className="h-4 w-4" checked={value === v} onChange={() => onChange(value === v ? "" : v)} data-testid={`sale-${name}-${v}`} />
        {l}
      </label>
    ))}
  </div>
);

const DatePick = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="field inline-flex !w-auto min-w-[220px] items-center gap-2 !py-2.5 text-left text-sm" data-testid="sale-collection-date">
            <CalendarDays size={16} /> {date ? format(date, "d MMMM yyyy") : "Choose a date"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto bg-paper p-0" align="start" data-testid="sale-collection-date-calendar">
          <Calendar mode="single" selected={date} defaultMonth={date} weekStartsOn={1} initialFocus
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
        </PopoverContent>
      </Popover>
      {value && <button type="button" onClick={() => onChange("")} aria-label="Clear date" className="p-1 text-charcoal/75 hover:text-red-800" data-testid="sale-collection-date-clear"><X size={16} /></button>}
    </div>
  );
};

export function CollectionDelivery({ f, set, setHandover }) {
  return (
    <fieldset className="mb-6" data-testid="sale-collection-delivery">
      <legend className="mb-3 text-sm font-semibold text-forest">Collection / delivery (Part H)</legend>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <span className={LABEL}>Delivered or collected</span>
          <Ticks name="handover" value={f.handover_method} options={[["collected", "Collected"], ["delivered", "Delivered"]]}
            onChange={setHandover} />
        </div>
        {f.handover_method === "delivered" && (
          <div>
            <span className={LABEL}>If delivered</span>
            <Ticks name="delivery" value={f.delivery_mode} options={[["driven", "Driven"], ["trailered", "Trailered"]]} onChange={set("delivery_mode")} />
          </div>
        )}
        {f.handover_method === "delivered" && (
          <label className="sm:col-span-2">
            <span className={LABEL}>Delivery address</span>
            <textarea rows={3} value={f.delivery_address} onChange={(e) => set("delivery_address")(e.target.value)} className="field text-sm"
              placeholder="Leave blank to fill in by hand" data-testid="sale-delivery-address" />
            {f.buyer_address && !f.delivery_address && (
              <button type="button" onClick={() => set("delivery_address")([f.buyer_address, f.buyer_postcode].filter(Boolean).join("\n"))}
                className="mt-1.5 text-sm font-semibold text-forest" data-testid="sale-delivery-address-copy">Same as the buyer's address</button>
            )}
          </label>
        )}
        <div className="sm:col-span-2">
          <span className={LABEL}>Date of collection or delivery</span>
          <DatePick value={f.collection_date} onChange={set("collection_date")} />
        </div>
      </div>
    </fieldset>
  );
}
